import React, { useEffect } from 'react';

const ObserverPattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Observer Pattern - Behavioral Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Observer Pattern - define one-to-many dependency between objects for event-driven architecture. Learn with news subscription and UI examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Observer Pattern - define one-to-many dependency between objects for event-driven architecture. Learn with news subscription and UI examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'observer pattern, design patterns, behavioral patterns, event driven, publish subscribe, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'observer pattern, design patterns, behavioral patterns, event driven, publish subscribe, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Observer Pattern</h1>
        <p style={styles.subtitle}>
          Behavioral Design Pattern - Define one-to-many dependency between objects
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Observer pattern defines a one-to-many dependency between objects so that when one 
            object changes state, all its dependents are notified and updated automatically. It's 
            the foundation of event-driven programming.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to notify multiple objects about changes in another object's state. Direct 
            coupling between objects makes the system rigid and hard to maintain. The Observer 
            pattern provides a way to achieve loose coupling.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: News Subscription</h2>
          <div style={styles.codeBlock}>
{`// Observer interface
interface Observer {
    void update(String news);
}

// Subject interface
interface Subject {
    void subscribe(Observer observer);
    void unsubscribe(Observer observer);
    void notifyObservers();
}

// Concrete subject
class NewsAgency implements Subject {
    private List<Observer> observers;
    private String latestNews;
    
    public NewsAgency() {
        this.observers = new ArrayList<>();
    }
    
    @Override
    public void subscribe(Observer observer) {
        observers.add(observer);
        System.out.println("Observer subscribed");
    }
    
    @Override
    public void unsubscribe(Observer observer) {
        observers.remove(observer);
        System.out.println("Observer unsubscribed");
    }
    
    @Override
    public void notifyObservers() {
        for (Observer observer : observers) {
            observer.update(latestNews);
        }
    }
    
    public void setNews(String news) {
        this.latestNews = news;
        notifyObservers();
    }
    
    public String getNews() {
        return latestNews;
    }
}

// Concrete observers
class NewsChannel implements Observer {
    private String name;
    
    public NewsChannel(String name) {
        this.name = name;
    }
    
    @Override
    public void update(String news) {
        System.out.println(name + " received news: " + news);
        broadcast(news);
    }
    
    private void broadcast(String news) {
        System.out.println(name + " is broadcasting: " + news);
    }
}

class OnlineNewspaper implements Observer {
    private String name;
    
    public OnlineNewspaper(String name) {
        this.name = name;
    }
    
    @Override
    public void update(String news) {
        System.out.println(name + " received news: " + news);
        publishOnline(news);
    }
    
    private void publishOnline(String news) {
        System.out.println(name + " published online: " + news);
    }
}

class MobileApp implements Observer {
    private String appName;
    
    public MobileApp(String appName) {
        this.appName = appName;
    }
    
    @Override
    public void update(String news) {
        System.out.println(appName + " received news: " + news);
        sendPushNotification(news);
    }
    
    private void sendPushNotification(String news) {
        System.out.println(appName + " sent push notification: " + news);
    }
}

// Usage
public class ObserverDemo {
    public static void main(String[] args) {
        // Create news agency
        NewsAgency agency = new NewsAgency();
        
        // Create observers
        NewsChannel cnn = new NewsChannel("CNN");
        OnlineNewspaper newspaper = new OnlineNewspaper("Daily News");
        MobileApp newsApp = new MobileApp("NewsApp");
        
        // Subscribe observers
        agency.subscribe(cnn);
        agency.subscribe(newspaper);
        agency.subscribe(newsApp);
        
        // Publish news
        agency.setNews("Breaking: New technology breakthrough!");
        
        System.out.println("\\n--- Unsubscribing CNN ---\\n");
        agency.unsubscribe(cnn);
        
        // Publish another news
        agency.setNews("Update: Market reaches new high!");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Loose Coupling:</strong> Subject and observers are loosely coupled</li>
            <li style={styles.listItem}><strong>Dynamic Relationships:</strong> Can add/remove observers at runtime</li>
            <li style={styles.listItem}><strong>Open/Closed Principle:</strong> Can add new observers without changing subject</li>
            <li style={styles.listItem}><strong>Broadcast Communication:</strong> Subject can notify multiple observers</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When changes to one object require updating multiple objects</li>
            <li style={styles.listItem}>When you want to notify objects without knowing who they are</li>
            <li style={styles.listItem}>When you need to implement event-driven systems</li>
            <li style={styles.listItem}>When you want to achieve loose coupling between objects</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Observer pattern is fundamental for event-driven programming and building responsive 
            systems. It enables loose coupling between objects while maintaining the ability to 
            notify multiple dependents of state changes.
          </p>
        </section>
      </main>
    </article>
  );
};

export default ObserverPattern;